import Phaser from 'phaser';
import { getActiveStudent } from '@/systems/ActiveStudent';
import { getCurrentSession } from '@/systems/AuthService';
import { saveSystem } from '@/systems/SaveSystem';
import { addBackdrop } from '@/ui/background';
import { Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';

/**
 * PreloadScene — exibe a splash com barra de progresso enquanto carrega
 * assets globais (fontes, atlas comuns, SFX de UI).
 *
 * A barra está totalmente cabeada aos eventos do Loader, mesmo que hoje
 * carreguemos zero assets — assim, quando começarmos a popular `preload()`,
 * o feedback visual já funciona.
 *
 * Uso de delay artificial (~600ms): com a fila vazia o Loader completa em
 * <1 frame e a splash some antes da criança ler o título. O delay é leve e
 * desaparece naturalmente quando assets reais começam a entrar.
 */
export class PreloadScene extends Phaser.Scene {
  private progressFill?: Phaser.GameObjects.Graphics;
  private progressFrame?: Phaser.GameObjects.Graphics;
  private percentLabel?: Phaser.GameObjects.Text;

  private readonly barWidth = 480;
  private readonly barHeight = 24;

  constructor() {
    super({ key: 'PreloadScene' });
  }

  preload(): void {
    addBackdrop(this);
    fadeInScene(this);
    this.buildSplash();

    this.load.on(Phaser.Loader.Events.PROGRESS, this.onProgress, this);
    this.load.on(Phaser.Loader.Events.COMPLETE, this.onComplete, this);

    // TODO: preload de assets globais virá nas próximas iterações.
  }

  create(): void {
    // Loader pode terminar antes do create rodar quando a fila está vazia.
    // O delayedCall garante que a splash fique visível por um instante.
    this.time.delayedCall(600, () => {
      void this.routeAfterSplash();
    });
  }

  /**
   * Roteamento condicional após a splash:
   *   - sem sessão Supabase           → AuthScene
   *   - com sessão, sem aluno ativo   → ProfileSelectScene
   *   - com sessão e aluno ativo      → MenuScene
   */
  private async routeAfterSplash(): Promise<void> {
    let nextScene = 'AuthScene';
    try {
      const session = await getCurrentSession();
      if (session) {
        const active = getActiveStudent();
        if (active) {
          await saveSystem.bindToStudent(active.id);
          nextScene = 'MenuScene';
        } else {
          nextScene = 'ProfileSelectScene';
        }
      }
    } catch (err) {
      console.error('[PreloadScene] Falha ao consultar sessão Supabase', err);
    }
    fadeToScene(this, nextScene);
  }

  private buildSplash(): void {
    const { width, height } = this.scale;

    this.add
      .text(width / 2, height / 2 - 80, 'Tabuada Geek', {
        fontFamily: Typography.fontFamily,
        fontSize: '72px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height / 2 - 10, 'by SuperGeeks', {
        fontFamily: Typography.fontFamily,
        fontSize: '24px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    const barX = width / 2 - this.barWidth / 2;
    const barY = height / 2 + 60;

    this.progressFrame = this.add.graphics();
    this.progressFrame.lineStyle(2, Palette.textMuted, 1);
    this.progressFrame.strokeRoundedRect(barX - 2, barY - 2, this.barWidth + 4, this.barHeight + 4, 8);

    this.progressFill = this.add.graphics();

    this.percentLabel = this.add
      .text(width / 2, barY + this.barHeight + 24, '0%', {
        fontFamily: Typography.fontFamily,
        fontSize: '18px',
        color: Palette.textDimCss,
      })
      .setOrigin(0.5);

    this.drawProgress(0);
  }

  private onProgress(value: number): void {
    this.drawProgress(value);
  }

  private onComplete(): void {
    this.drawProgress(1);
  }

  private drawProgress(value: number): void {
    if (!this.progressFill || !this.percentLabel) return;
    const { width, height } = this.scale;
    const barX = width / 2 - this.barWidth / 2;
    const barY = height / 2 + 60;

    this.progressFill.clear();
    this.progressFill.fillStyle(Palette.primary, 1);
    this.progressFill.fillRoundedRect(barX, barY, this.barWidth * value, this.barHeight, 6);

    this.percentLabel.setText(`${Math.round(value * 100)}%`);
  }
}
